'use client';

import { twMerge } from 'tailwind-merge';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'title' | 'avatar' | 'card' | 'button' | 'image';
  lines?: number;
  width?: string;
  height?: string;
}

function Skeleton({ className, variant = 'text', lines = 1, width, height }: SkeletonProps) {
  const baseClasses = 'animate-pulse bg-grayscale-2 rounded-lg';

  const variantClasses = {
    text: 'h-4',
    title: 'h-6',
    avatar: 'h-12 w-12 rounded-full',
    card: 'h-32 w-full',
    button: 'h-10 w-24',
    image: 'h-48 w-full',
  };

  const getWidth = () => {
    if (width) return width;
    switch (variant) {
      case 'text':
        return 'w-full';
      case 'title':
        return 'w-3/4';
      case 'avatar':
        return 'w-12';
      case 'card':
      case 'image':
        return 'w-full';
      case 'button':
        return 'w-24';
      default:
        return 'w-full';
    }
  };

  const getHeight = () => {
    if (height) return height;
    return variantClasses[variant];
  };

  if (lines > 1 && (variant === 'text' || variant === 'title')) {
    return (
      <div className={twMerge('space-y-2', className)}>
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={twMerge(
              baseClasses,
              getHeight(),
              getWidth(),
              index === lines - 1 && variant === 'text' ? 'w-2/3' : '',
            )}
          />
        ))}
      </div>
    );
  }

  return <div className={twMerge(baseClasses, getHeight(), getWidth(), className)} />;
}

// Convenience components for common use cases
export function TextSkeleton({ lines = 1, className }: { lines?: number; className?: string }) {
  return <Skeleton variant='text' lines={lines} className={className} />;
}

export function TitleSkeleton({ lines = 1, className }: { lines?: number; className?: string }) {
  return <Skeleton variant='title' lines={lines} className={className} />;
}

export function AvatarSkeleton({ className }: { className?: string }) {
  return <Skeleton variant='avatar' className={className} />;
}

export function CardSkeleton({ className }: { className?: string }) {
  return <Skeleton variant='card' className={className} />;
}

export function ButtonSkeleton({ className }: { className?: string }) {
  return <Skeleton variant='button' className={className} />;
}

export function ImageSkeleton({ className }: { className?: string }) {
  return <Skeleton variant='image' className={className} />;
}

// Complex skeleton for content areas
export function ContentSkeleton({ className }: { className?: string }) {
  return (
    <div className={twMerge('space-y-4', className)}>
      <TitleSkeleton lines={2} />
      <TextSkeleton lines={3} />
      <div className='flex gap-4'>
        <AvatarSkeleton />
        <div className='flex-1 space-y-2'>
          <TextSkeleton lines={2} />
        </div>
      </div>
    </div>
  );
}

export default Skeleton;

// Skeleton for list items
export function ListItemSkeleton({ className }: { className?: string }) {
  return (
    <div className={twMerge('flex items-center space-x-4', className)}>
      <AvatarSkeleton />
      <div className='flex-1 space-y-2'>
        <TitleSkeleton />
        <TextSkeleton lines={2} />
      </div>
    </div>
  );
}
